import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';

import {
  ContractDocumentProviders,
  ContractDocumentStatus,
  ContractExportInformation,
  ContractRecipient,
  ContractSendProgressMessage,
  ContractStatus,
} from 'types';

import { api } from './api';

/**
 * LaunchDarkly kill-switch for the v2 async contract-send flow.
 *
 * Default is `false` (v1 synchronous route) so flipping the flag off — or
 * losing the LD connection — reverts to the legacy `POST /accounts/:id/
 * contracts/current/send` path with no SSE progress channel. The v2 codepath
 * (async queue + SSE) is opt-in.
 */
export const useContractsV2Enabled = (): boolean => {
  const { contractsV2Enabled = false } = useFlags();
  return contractsV2Enabled;
};

/**
 * Client-side representation of a contract from the API.
 * Uses string IDs instead of ObjectId since the API serializes them.
 */
export interface ClientContract {
  id: string;
  accountId: string;
  quoteId: string;
  externalReference?: string;
  isEPCContract?: boolean;
  contractDocuments: ClientContractDocument[];
  status: ContractStatus;
}

/** Client-side representation of a contract document */
export interface ClientContractDocument {
  provider: ContractDocumentProviders;
  status: ContractDocumentStatus;
  providerData?: {
    templateId?: string | null;
    envelopeId?: string;
    status?: string;
    shouldExport?: boolean;
    exportStatus?: string;
  };
  recipients?: ContractRecipient[];
}

export const contractApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCurrentContract: build.query<ClientContract, string>({
      query: (accountId) => ({ url: `accounts/${accountId}/contracts/current` }),
      providesTags: (_result, _error, id) => [{ type: 'Contracts', id }],
    }),
    getContracts: build.query<ClientContract[], string>({
      query: (accountId) => ({ url: `accounts/${accountId}/contracts` }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Contracts' as const, id })), { type: 'Contracts', id: 'LIST' }]
          : [{ type: 'Contracts', id: 'LIST' }],
    }),
    getContractExportStatus: build.query<ContractExportInformation, { accountId: string; contractId: string }>({
      query: ({ accountId, contractId }) => ({ url: `accounts/${accountId}/contracts/${contractId}/export-status` }),
      providesTags: (_result, _error, args) => [{ type: 'ContractExportStatus', id: args.contractId }],
    }),
    sendContract: build.mutation<
      ClientContract,
      { accountId: string; contractId: string; forceSend?: boolean; externalReference?: string; v2?: boolean }
    >({
      query: ({ accountId, forceSend, externalReference, v2 = false }) =>
        v2
          ? {
              url: `v2/accounts/${accountId}/contracts/current/send`,
              method: 'POST',
              params: { force: forceSend },
              body: { externalReference },
            }
          : {
              url: `accounts/${accountId}/contracts/current/send?force=${forceSend ?? false}`,
              method: 'POST',
            },
      async onQueryStarted({ accountId, v2 = false }, { dispatch, queryFulfilled }) {
        // SSE progress only exists on the v2 route; v1 returns a fully-resolved
        // ClientContract synchronously and relies on invalidatesTags for refresh.
        if (!v2) return;
        try {
          const { data: contract } = await queryFulfilled;
          dispatch(
            contractApi.util.updateQueryData('getCurrentContract', accountId, (draft) => {
              Object.assign(draft, contract);
            }),
          );
          dispatch(contractApi.endpoints.getContractSendProgress.initiate({ accountId, contract }));
        } catch {
          // Mutation failed — invalidatesTags will trigger a refetch; nothing to do here.
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Accounts', id: arg.accountId },
        { type: 'AccountMilestones', id: arg.accountId },
        { type: 'Contracts', id: arg.contractId },
      ],
    }),
    getContractSendProgress: build.query<ClientContract, { accountId: string; contract: ClientContract }>({
      queryFn: ({ contract }) => ({ data: contract }),
      async onCacheEntryAdded(
        { accountId, contract },
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData, dispatch },
      ) {
        let eventSource: EventSource | null = null;
        const applyStatus = (status: ContractStatus, shouldRefetch = false) => {
          updateCachedData((draft) => {
            draft.status = status;
          });
          dispatch(
            contractApi.util.updateQueryData('getCurrentContract', accountId, (draft) => {
              draft.status = status;
            }),
          );
          dispatch(
            contractApi.util.updateQueryData('getContracts', accountId, (draft) => {
              const matchingContract = draft.find((item) => item.id === contract.id);
              if (matchingContract) {
                matchingContract.status = status;
              }
            }),
          );
          if (shouldRefetch) {
            dispatch(
              contractApi.util.invalidateTags([
                { type: 'Contracts', id: accountId },
                { type: 'Contracts', id: contract.id },
              ]),
            );
          }
        };

        try {
          await cacheDataLoaded;
          eventSource = new EventSource(`/api/v2/accounts/${accountId}/contracts/current/send/progress`);
          eventSource.addEventListener('message', (event: MessageEvent) => {
            try {
              const message = JSON.parse(event.data) as ContractSendProgressMessage;
              if (message.status === 'sending') {
                applyStatus(ContractStatus.sending);
              }
              if (message.status === 'sent') {
                applyStatus(ContractStatus.sent, true);
                eventSource?.close();
                eventSource = null;
              }
              if (message.status === 'failed') {
                applyStatus(ContractStatus.created, true);
                eventSource?.close();
                eventSource = null;
              }
            } catch {
              // Ignore malformed progress messages.
            }
          });
          eventSource.addEventListener('error', () => {
            eventSource?.close();
            eventSource = null;
          });
        } catch {
          // cacheDataLoaded rejected because the entry was removed before it resolved.
        }

        await cacheEntryRemoved;
        eventSource?.close();
      },
    }),
    getContractSigningUrl: build.query<{ url: string }, { accountId: string; returnUrl: string }>({
      query: ({ accountId, returnUrl }) => ({
        url: `v2/accounts/${accountId}/contracts/current/signing-url`,
        params: { returnUrl },
      }),
    }),
    voidContract: build.mutation<void, { accountId: string; contractId: string }>({
      query: ({ accountId, contractId }) => ({
        url: `accounts/${accountId}/contracts/${contractId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Accounts', id: arg.accountId },
        { type: 'Contracts', id: 'LIST' },
        { type: 'AccountMilestones', id: arg.accountId },
        { type: 'DocumentDocument', id: 'LIST' },
        { type: 'Quotes', id: 'LIST' },
        { type: 'ChangeOrders', id: arg.accountId },
      ],
    }),
    reinstateContract: build.mutation<void, { accountId: string; externalReference: string }>({
      query: ({ accountId, externalReference }) => ({
        url: `accounts/${accountId}/contracts/${externalReference}`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Accounts', id: arg.accountId },
        { type: 'Contracts', id: 'LIST' },
        { type: 'AccountMilestones', id: arg.accountId },
        { type: 'DocumentDocument', id: 'LIST' },
        { type: 'Quotes', id: 'LIST' },
        { type: 'ChangeOrders', id: arg.accountId },
      ],
    }),
    checkDisclosureStatus: build.mutation<ClientContract, { accountId: string; contractId: string }>({
      query: ({ accountId, contractId }) => ({
        url: `accounts/${accountId}/contracts/${contractId}/disclosure/status`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Accounts', id: arg.accountId },
        { type: 'Contracts', id: 'LIST' },
        { type: 'AccountMilestones', id: arg.accountId },
        { type: 'DocumentDocument', id: 'LIST' },
      ],
    }),
    syncContract: build.mutation<ClientContract, { accountId: string; contractId: string }>({
      query: ({ accountId, contractId }) => ({
        url: `accounts/${accountId}/contracts/${contractId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Accounts', id: arg.accountId },
        { type: 'Contracts', id: 'LIST' },
        { type: 'AccountMilestones', id: arg.accountId },
        { type: 'DocumentDocument', id: 'LIST' },
      ],
    }),
  }),
});

const { useSendContractMutation: useSendContractMutationInternal } = contractApi;

export const {
  useGetContractSendProgressQuery,
  useLazyGetContractSigningUrlQuery,
  useGetContractsQuery,
  useLazyGetContractExportStatusQuery,
  useGetCurrentContractQuery,
  useVoidContractMutation,
  useCheckDisclosureStatusMutation,
  useReinstateContractMutation,
  useSyncContractMutation,
} = contractApi;

/**
 * Public send-contract mutation hook. Reads the `contractsV2Enabled`
 * LaunchDarkly flag and injects the `v2` arg into every trigger call so the
 * underlying endpoint routes to either the v1 sync path or the v2 async path.
 *
 * The hook name keeps the `V2` suffix for backwards compatibility with the
 * call sites that were migrated in the original async-send PR — they remain
 * unchanged; only the URL the trigger hits varies with the flag.
 */
export const useSendContractV2Mutation = ((options?: Parameters<typeof useSendContractMutationInternal>[0]) => {
  const v2 = useContractsV2Enabled();
  const [trigger, result] = useSendContractMutationInternal(options);
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useSendContractMutationInternal>;
}) as typeof useSendContractMutationInternal;
